import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useStore } from '../../../../../stores/root';

// assets
import { faHome, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';

// props

// components
import { CommandBar, DefaultButton, ICommandBarItemProps, PrimaryButton, Stack } from '@fluentui/react';
import DetailsStack, { IStackSectionProps } from '../../../../uiframeworks/detailsStack';
import LoadingComponent from '../../../../feedbacks/loading';
import NoAccess from '../../../../uiframeworks/noAccess';
import { IVesselRequisitionResourceProps } from '../../../../../props/vessels/requisitions/requisition';
import VesselRequisitionService from '../../../../../services/vessels/requisitions/requisition';
import VesselRequisitionDetailProperties from './properties';
import VesselRequisitionItemsList from '../../items/general/list';

interface VesselRequisitionDetailsProps {
    requisitionId: string;
}

const VesselRequisitionDetails: React.FC<VesselRequisitionDetailsProps> = observer((props: VesselRequisitionDetailsProps) => {
    const { banner, topbar, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [requisition, setRequisition] = React.useState<IVesselRequisitionResourceProps | undefined>();
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [hasPermission, setHasPermission] = React.useState<boolean>(false);

    useEffect(() => {
        _onGetVesselRequisition();
    }, []);

    const _onGetVesselRequisition = async () => {
        try {
            setLoaded(false);

            const _requisition = await VesselRequisitionService.read(props.requisitionId);
            setRequisition(_requisition);

            topbar.show(_requisition.name, [
                { key: "", icon: faHome, href: "/" },
                { key: 'vessels', href: "/vessels", text: 'Vessels' },
                { key: _requisition.vessel.id, href: "/vessels/" + _requisition.vessel.id, text: _requisition.vessel.name },
                { key: _requisition.id, text: _requisition.name }
            ]);

            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'get_vessel_requisition__error',
                text: 'Failed to get vessel requisition details. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const getCommandBarItems = () => {
        const items: ICommandBarItemProps[] = [];
        const farItems: ICommandBarItemProps[] = [];

        items.push({
            key: 'update',
            text: 'Update details',
            onRender: () => {
                return <PrimaryButton text={'Update details'}
                    iconProps={{ iconName: "Edit" }} />
            }
        });

        items.push({
            key: 'download',
            text: 'Download',
            onRender: () => <DefaultButton text={'Download'} iconProps={{ iconName: "Download" }} onClick={() => { }} styles={{ root: { marginLeft: 10 } }} />
        });

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        if (refresh) { _onGetVesselRequisition() }
    }

    const leftSections = (_requisition: IVesselRequisitionResourceProps): IStackSectionProps[] => {
        return [
            {
                key: 'tabs',
                isCard: true,
                tabs: [
                    {
                        key: 'items',
                        title: 'Items List',
                        content: <VesselRequisitionItemsList requisitionId={_requisition.id} />
                    }
                ]
            }
            /*{
                key: 'secondrow',
                subsections: [
                    {
                        key: 'commissions',
                        width: '50%',
                        header: { title: 'Commissions Breakdown' },
                        isCard: true,
                        content: <EmployeeContractDetailsCommissions contract={_contract} />
                    },
                    {
                        key: 'placeholder'
                    }
                ]
            }*/
        ];
    }

    const rightSections = (_requisition: IVesselRequisitionResourceProps): IStackSectionProps[] => {
        const detailsSection: IStackSectionProps = {
            key: 'details',
            header: {
                title: 'Details',
                buttons: []
            },
            isCard: true,
            content: <VesselRequisitionDetailProperties requisition={_requisition} />
        };

        return [detailsSection];
    }

    return <Stack tokens={{ childrenGap: 20 }}>
        {!loaded ? <LoadingComponent label='Retrieving vessel details ...' labelPosition='right' spinnerPosition='baseline' /> : null}
        {loaded && requisition ? <>
            <CommandBar
                items={getCommandBarItems().items}
                farItems={getCommandBarItems().farItems}
                styles={{
                    root: {
                        padding: 0,
                        height: 'unset',
                        backgroundColor: 'transparent'
                    }
                }}
                ariaLabel="Use left and right arrow keys to navigate between commands" />
            <Stack tokens={{ childrenGap: 20 }}>
                <DetailsStack left={leftSections(requisition)} right={rightSections(requisition)} rightWidth={300} />
            </Stack>
        </> : null}
    </Stack>;
});

export default VesselRequisitionDetails;
